'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowRight } from '@byway/icons';
import { useSearchParams } from 'next/navigation';
import path from 'path';
import { StructuredText } from 'react-datocms';
import { shortDayShortMonth } from 'utils/DateFormats';
import { ChipHeader } from '~/components/ChipHeader';
import { Divider } from '~/components/Divider';
import { JourneyTimes } from '~/components/JourneyTimes';
import { LinkList } from '~/components/LinkList';
import { MessagePanel } from '~/components/MessagePanel';
import { isNotEmptyString } from '~/utils/datoHelpers';
import type { LegProps } from './Leg.types';
const Leg = t0 => {
  const $ = _c(69);
  const {
    isDetailed,
    isMinimal,
    leg,
    showChips,
    showTickets
  } = t0;
  const {
    arrivalTime,
    bookingReference,
    departureTime,
    destination,
    destinationTerminalName,
    endDate,
    goodToKnowAutomatedContent,
    transportClass,
    keyInformation,
    leg: featuredLeg,
    modeOfTransports,
    origin,
    originTerminalName,
    startDate,
    tickets,
    ticketType,
    type
  } = leg;
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("summary");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const summary = t1;
  const isSummary = summary === "true";
  let t2;
  if ($[2] !== bookingReference || $[3] !== isDetailed || $[4] !== ticketType) {
    t2 = isDetailed && (ticketType === "ticket" || ticketType === "reservation") && isNotEmptyString(bookingReference);
    $[2] = bookingReference;
    $[3] = isDetailed;
    $[4] = ticketType;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const showBookingRef = t2;
  let t3;
  let uniqueModes;
  if ($[6] !== modeOfTransports) {
    const modeIcons = {
      RAIL: "train",
      TRAIN: "train",
      TRAM: "train",
      WALK: "walk",
      BUS: "bus",
      COACH: "bus",
      FERRY: "ferry",
      TAXI: "taxi",
      SLEEPER: "sleeper-train",
      OVERNIGHT_BUS: "overnight-bus",
      OVERNIGHT_FERRY: "overnight-ferry"
    };
    const t4 = new Set(modeOfTransports);
    let t5;
    if ($[9] !== t4) {
      t5 = [...t4].filter(_temp);
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    uniqueModes = t5;
    t3 = uniqueModes.map(transport => {
      const iconName = modeIcons[transport];
      return iconName;
    });
    $[6] = modeOfTransports;
    $[7] = t3;
    $[8] = uniqueModes;
  } else {
    t3 = $[7];
    uniqueModes = $[8];
  }
  const icons = t3;
  const isFeaturedLeg = featuredLeg !== null;
  let t4;
  if ($[11] !== startDate) {
    t4 = shortDayShortMonth(startDate);
    $[11] = startDate;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = `${departureTime} - ${arrivalTime}`;
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = [t4, t5];
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const dateAndTime = t6;
  let t7;
  if ($[16] !== startDate) {
    t7 = shortDayShortMonth(startDate);
    $[16] = startDate;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const formattedStartDate = t7;
  let t8;
  if ($[18] !== endDate || $[19] !== startDate) {
    t8 = startDate === endDate ? "" : shortDayShortMonth(endDate);
    $[18] = endDate;
    $[19] = startDate;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const formattedEndDate = t8;
  let tags;
  if ($[21] !== dateAndTime || $[22] !== isSummary || $[23] !== transportClass || $[24] !== uniqueModes) {
    tags = [...uniqueModes, ...dateAndTime];
    if (isSummary && transportClass) {
      tags.push(transportClass);
    }
    $[21] = dateAndTime;
    $[22] = isSummary;
    $[23] = transportClass;
    $[24] = uniqueModes;
    $[25] = tags;
  } else {
    tags = $[25];
  }
  const getFilename = _temp2;
  let t9;
  if ($[26] !== icons || $[27] !== showChips || $[28] !== tags || $[29] !== type) {
    t9 = showChips && <ChipHeader type={type} tags={tags} icons={icons} />;
    $[26] = icons;
    $[27] = showChips;
    $[28] = tags;
    $[29] = type;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== destination || $[32] !== isDetailed || $[33] !== origin) {
    t10 = isDetailed ? <h1 className="typography-title-5 pt-16px"><span>{origin} </span><ArrowRight ariaLabel="to" strokeWidth={2} isInline={true} /> <span>{destination}</span></h1> : <h2 className="typography-title-6 pt-16px">{origin} <ArrowRight ariaLabel="to" strokeWidth={2} isInline={true} /> {destination}</h2>;
    $[31] = destination;
    $[32] = isDetailed;
    $[33] = origin;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t9) {
    t11 = <div className="print:break-inside-avoid">{t9}{t10}</div>;
    $[35] = t10;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== featuredLeg || $[39] !== isFeaturedLeg || $[40] !== isMinimal) {
    t12 = isFeaturedLeg && !isMinimal && <p className="pb-16px typography-body-1">{featuredLeg.description}</p>;
    $[38] = featuredLeg;
    $[39] = isFeaturedLeg;
    $[40] = isMinimal;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== arrivalTime || $[43] !== departureTime || $[44] !== destination || $[45] !== destinationTerminalName || $[46] !== formattedEndDate || $[47] !== formattedStartDate || $[48] !== isDetailed || $[49] !== origin || $[50] !== originTerminalName) {
    t13 = arrivalTime && departureTime && <><Divider variant="horizontal" /><JourneyTimes departureTime={departureTime} arrivalTime={arrivalTime} origin={originTerminalName || origin} destination={destinationTerminalName || destination} startDate={formattedStartDate} endDate={formattedEndDate} isDateShowing={isDetailed} /></>;
    $[42] = arrivalTime;
    $[43] = departureTime;
    $[44] = destination;
    $[45] = destinationTerminalName;
    $[46] = formattedEndDate;
    $[47] = formattedStartDate;
    $[48] = isDetailed;
    $[49] = origin;
    $[50] = originTerminalName;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== bookingReference || $[53] !== showBookingRef) {
    t14 = showBookingRef && <><Divider variant="horizontal" /><h2 className="typography-chip-style text-neutral-600 mb-8px mt-12px">Confirmation number</h2><p className="typography-emphasis-1 pb-12px">{bookingReference}</p></>;
    $[52] = bookingReference;
    $[53] = showBookingRef;
    $[54] = t14;
  } else {
    t14 = $[54];
  }
  let t15;
  if ($[55] !== goodToKnowAutomatedContent || $[56] !== keyInformation) {
    t15 = (isNotEmptyString(keyInformation) || goodToKnowAutomatedContent?.length > 0) && <><Divider variant="horizontal" /><h3 className="mt-12px mb-12px typography-chip-style text-neutral-600">Important information</h3>{isNotEmptyString(keyInformation) && <p className="typography-body-2 mb-20px" dangerouslySetInnerHTML={{
        __html: keyInformation
      }} />}{goodToKnowAutomatedContent && goodToKnowAutomatedContent.length > 0 && <div className="space-y-4 typography-body-2 mb-20px prose-a:underline">{goodToKnowAutomatedContent.map(_temp3)}</div>}</>;
    $[55] = goodToKnowAutomatedContent;
    $[56] = keyInformation;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== showTickets || $[59] !== ticketType || $[60] !== tickets) {
    t16 = showTickets && <><Divider variant="horizontal" /><h2 className="mt-3 typography-chip-style text-neutral-600">{ticketType === "ticket" && "Tickets"}{ticketType === "reservation" && "Reservations"}{ticketType === "tod" && "Ticket Booking Confirmation"}</h2>{tickets && tickets.length > 0 && <LinkList capitalized={true} items={tickets.map(ticket => ({
        url: ticket.url,
        title: getFilename(ticket.url)
      }))} />}</>;
    $[58] = showTickets;
    $[59] = ticketType;
    $[60] = tickets;
    $[61] = t16;
  } else {
    t16 = $[61];
  }
  let t17;
  if ($[62] !== t11 || $[63] !== t12 || $[64] !== t13 || $[65] !== t14 || $[66] !== t15 || $[67] !== t16) {
    t17 = <div>{t11}{t12}{t13}{t14}{t15}{t16}</div>;
    $[62] = t11;
    $[63] = t12;
    $[64] = t13;
    $[65] = t14;
    $[66] = t15;
    $[67] = t16;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  return t17;
};
export { Leg };
function _temp(icon) {
  return icon !== "WALK";
}
function _temp2(url) {
  const filename = path.parse(url).name.replace(/[^-]*-/, "");
  const title = filename.replace(/-/g, " ");
  return title;
}
function _temp3(content, i) {
  return <MessagePanel status={content.category} key={i} message={<StructuredText data={content.content} />} />;
}